import React, { useEffect, useState } from "react";
import { Menu, X, Mail } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const NAV_LINKS = [
    { id: "about", label: "About" },
    { id: "approach", label: "Approach" },
    { id: "why", label: "Why us" },
    { id: "solutions", label: "Solutions" },
    { id: "market", label: "Market" },
    { id: "pricing", label: "Pricing" },
    { id: "team", label: "Team" },
];

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_bio-guardian/artifacts/slqogudy_Emblem.jpeg";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    const handleNav = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setMobileOpen(false);
    };

    return (
        <>
            <header
                data-testid="site-navbar"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                    scrolled || mobileOpen
                        ? "py-3 backdrop-blur-xl bg-background/80 border-b border-border/60"
                        : "py-5 bg-transparent"
                }`}
            >
                <nav className="container-x flex items-center justify-between gap-4">
                    <button
                        data-testid="nav-logo"
                        onClick={() => handleNav("hero")}
                        className="flex items-center gap-3 lg:gap-4 group min-w-0"
                    >
                        <span className="relative h-10 w-10 lg:h-14 lg:w-14 shrink-0 overflow-hidden rounded-full ring-1 ring-border bg-white shadow-md shadow-primary/10">
                            <img
                                src={LOGO_URL}
                                alt="Yaalee Biotech Solutions emblem"
                                className="h-full w-full object-cover scale-110 transition-transform group-hover:scale-125"
                            />
                        </span>
                        <span className="flex flex-col leading-none text-left">
                            <span className="font-display text-[18px] sm:text-[20px] lg:text-[30px] font-semibold tracking-tight">
                                Yaalee
                            </span>
                            <span className="hidden sm:inline text-[10px] lg:text-[12px] uppercase tracking-[0.24em] text-muted-foreground mt-1 lg:mt-2">
                                Biotech Solutions
                            </span>
                        </span>
                    </button>

                    {/* Desktop nav — hidden below lg (1024px) */}
                    <div className="hidden lg:flex items-center gap-1 rounded-full border border-border/70 bg-background/40 backdrop-blur-md px-2 py-1">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => handleNav(l.id)}
                                className="px-4 py-1.5 text-[13px] tracking-wide text-foreground/80 hover:text-foreground rounded-full hover:bg-primary/10 transition"
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <ThemeToggle />
                        <a
                            href="mailto:founders.yaalee@gmail.com"
                            data-testid="nav-contact-btn"
                            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-[13px] font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition"
                        >
                            Contact us
                        </a>

                        {/* Mobile menu trigger */}
                        <button
                            type="button"
                            data-testid="nav-mobile-toggle"
                            aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileOpen}
                            onClick={() => setMobileOpen((v) => !v)}
                            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/40 backdrop-blur-md hover:bg-primary/10 hover:border-primary/50 transition"
                        >
                            {mobileOpen ? (
                                <X className="h-[18px] w-[18px] text-foreground" />
                            ) : (
                                <Menu className="h-[18px] w-[18px] text-foreground" />
                            )}
                        </button>
                    </div>
                </nav>
            </header>

            {/* Mobile menu sheet */}
            <div
                data-testid="mobile-menu"
                className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
                    mobileOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
                aria-hidden={!mobileOpen}
            >
                <div
                    className="absolute inset-0 bg-background/85 backdrop-blur-xl"
                    onClick={() => setMobileOpen(false)}
                />
                <div className="relative h-full overflow-y-auto pt-28 pb-12 px-6">
                    <ul className="flex flex-col gap-2">
                        {NAV_LINKS.map((l, i) => (
                            <li
                                key={l.id}
                                className={`transition-all duration-500 ${
                                    mobileOpen
                                        ? "opacity-100 translate-y-0"
                                        : "opacity-0 translate-y-3"
                                }`}
                                style={{
                                    transitionDelay: mobileOpen
                                        ? `${i * 60 + 100}ms`
                                        : "0ms",
                                }}
                            >
                                <button
                                    data-testid={`mobile-nav-${l.id}`}
                                    onClick={() => handleNav(l.id)}
                                    className="w-full text-left flex items-baseline justify-between gap-4 border-b border-border/60 py-5"
                                >
                                    <span className="font-display text-3xl font-medium tracking-tight">
                                        {l.label}
                                    </span>
                                    <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
                                        0{i + 1}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 space-y-4">
                        <a
                            href="mailto:founders.yaalee@gmail.com"
                            data-testid="mobile-contact-btn"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center justify-center gap-2 w-full rounded-full bg-primary px-5 py-4 text-sm font-medium text-primary-foreground shadow-md hover:bg-primary/90 transition"
                        >
                            <Mail className="h-4 w-4" />
                            founders.yaalee@gmail.com
                        </a>
                        <p className="text-center text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                            Yaalee · Guardian of sustainable innovation
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
